import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Lazy-loaded (the reader carries the markdown + mermaid stack).
const PoliciesHome = React.lazy(() => import("./PoliciesHome"));
const PolicyEditor = React.lazy(() => import("./PolicyEditor"));
const PolicyHistory = React.lazy(() => import("./PolicyHistory"));
const PolicyActivityPage = React.lazy(() => import("./PolicyActivityPage"));
const PolicyAcknowledgements = React.lazy(() => import("./PolicyAcknowledgements"));
const PolicySettings = React.lazy(() => import("./PolicySettings"));

/**
 * Policies & Procedures — every employee reads published policies; a settings-page editor
 * roster edits them; publishes are versioned, emailed to staff and acknowledged.
 *
 * Route gates resolve against the backend manifest (src/modules/policies/permissions.ts):
 *   view  = EMPLOYEE + editors/admins   → every staff member
 *   edit  = POLICIES_EDIT/ADMIN          → the settings route
 * The EDITOR routes are `view`-gated on purpose: membership of the editor roster is DATA
 * (PolicySettings.editorClientIds) that a route can't express, so the pages check
 * `policyMyAccess.canEdit` themselves and hide (never disable) what the viewer can't do.
 */
const policiesModuleConfig: ModuleConfig = {
    id: "policies",
    name: "Policies & Procedures",
    description:
        "Published policies every employee can read; editors publish immutable versions, staff are emailed what changed and acknowledge what they read; every event has an audit URL.",
    version: "0.1.0",
    enabled: true,
    category: "business",
    order: 19,
    icon: "📋",
    color: "#1F4E79",

    routes: [
        {
            path: "/policies",
            component: PoliciesHome,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { noContainer: true, noFooter: true, fillViewport: true },
        },
        {
            path: "/policies/new",
            component: PolicyEditor,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.xl" },
        },
        {
            path: "/policies/settings",
            component: PolicySettings,
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/policies/activity/:activityId",
            component: PolicyActivityPage,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.md" },
        },
        {
            path: "/policies/:id/edit",
            component: PolicyEditor,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.xl" },
        },
        {
            path: "/policies/:id/history",
            component: PolicyHistory,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.xl" },
        },
        {
            path: "/policies/:id/acknowledgements",
            component: PolicyAcknowledgements,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/policies/:id",
            component: PoliciesHome,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { noContainer: true, noFooter: true, fillViewport: true },
        },
    ],

    navigation: [
        { label: "Policies & Procedures", path: "/policies", icon: "📋", permissionLevel: "view" },
        { label: "Policy settings", path: "/policies/settings", icon: "⚙️", permissionLevel: "edit" },
    ],

    // Backend is the SSOT — resolved at runtime via the modulePermissions query.
    permissions: { view: [], create: [], edit: [], delete: [] },
};

export default policiesModuleConfig;
